/**
 * HistoryTab Component
 *
 * Displays the full audit trail for an item.
 * - Desktop/tablet: compact table with horizontal scroll safety
 * - Phone: stacked card rows (actor + timestamp in footer)
 * - Search input when 8+ events (zero/one/few/many rule)
 * - Actor names resolved from users table (no raw UUIDs)
 * - Event-specific detail: from/to, attribute, cycle/session/related item context
 * - promotionCohortId suppressed (internal ID, no display value)
 * - targetItemIds resolved to item titles
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Clock,
  ArrowRight,
  GitBranch,
  CheckCircle,
  Circle,
  AlertCircle,
  FileEdit,
  Loader2,
  Lock,
  Unlock,
  Calendar,
  Layers,
  Camera,
  BarChart3,
  Users,
  Upload,
  Archive,
  Search,
  X,
  MessageSquare,
} from 'lucide-react';
import { useAuditStore, type AuditEntry } from '../../../../stores/prioritization/auditStore';
import { useEntityCommentsStore } from '../../../../stores/prioritization/entityCommentsStore';
import { plainExcerpt } from '../../../../lib/commentMentions';
import { useItemsStore } from '../../../../stores/prioritization/itemsStore';
import { useAllSessions } from '../../../../stores/prioritization/sessionStore';
import { useActorDisplayMap } from '../../../../hooks/prioritization/useActorDisplayMap';
import { getItemCountState } from '../../../../components/collection/types';
import { buildDescription, changeLines, namedPeopleIds } from './historyWords';

// ============================================================================
// Field-change display helpers
// ============================================================================

/** How many history events show at once. */
const HISTORY_PAGE_SIZE = 20;
// ============================================================================
// Event type config (icon + color)
// ============================================================================

const EVENT_CONFIG: Record<string, { icon: React.ElementType; color: string; bgColor: string }> = {
  comment_posted:              { icon: MessageSquare, color: 'text-[var(--text-sec)]', bgColor: 'bg-[var(--hover-tint)]' },
  SYNC_VALUE_SET_ASIDE:        { icon: GitBranch,   color: 'text-[var(--mustard)]', bgColor: 'bg-[var(--mustard-dim)]' },
  CREATE:                      { icon: Circle,      color: 'text-[var(--green)]',   bgColor: 'bg-[var(--green-dim)]' },
  item_created:                { icon: Circle,      color: 'text-[var(--green)]',   bgColor: 'bg-[var(--green-dim)]' },
  UPDATE:                      { icon: Clock,       color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  STATE_CHANGE:                { icon: ArrowRight,  color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]' },
  PROMOTE:                     { icon: CheckCircle, color: 'text-[var(--green)]',   bgColor: 'bg-[var(--green-dim)]' },
  item_promoted:               { icon: CheckCircle, color: 'text-[var(--green)]',   bgColor: 'bg-[var(--green-dim)]' },
  DEMOTE:                      { icon: AlertCircle, color: 'text-[var(--mustard)]',  bgColor: 'bg-[var(--mustard-dim)]' },
  DELETE:                      { icon: AlertCircle, color: 'text-[var(--coral)]',     bgColor: 'bg-[var(--coral-dim)]' },
  ITEM_PROMOTED:               { icon: CheckCircle, color: 'text-[var(--green)]',   bgColor: 'bg-[var(--green-dim)]' },
  VALUE_ACCEPTED:              { icon: CheckCircle, color: 'text-[var(--green)]',   bgColor: 'bg-[var(--green-dim)]' },
  value_proposed:              { icon: FileEdit,    color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  value_accepted:              { icon: CheckCircle, color: 'text-[var(--green)]',   bgColor: 'bg-[var(--green-dim)]' },
  value_rejected:              { icon: AlertCircle, color: 'text-[var(--mustard)]',  bgColor: 'bg-[var(--mustard-dim)]' },
  PROPOSAL_SUBMITTED:          { icon: FileEdit,    color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  lock:                        { icon: Lock,        color: 'text-[var(--text-sec)]',   bgColor: 'bg-[var(--hover-tint)]' },
  unlock:                      { icon: Unlock,      color: 'text-[var(--text-ter)]',   bgColor: 'bg-[var(--surface)]' },
  ValueLocked:                 { icon: Lock,        color: 'text-[var(--text-sec)]',   bgColor: 'bg-[var(--hover-tint)]' },
  item_transition:             { icon: ArrowRight,  color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]' },
  item_title_updated:          { icon: FileEdit,    color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  item_description_updated:    { icon: FileEdit,    color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  item_tags_updated:           { icon: FileEdit,    color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  item_work_type_updated:      { icon: Circle,      color: 'text-[var(--green)]',    bgColor: 'bg-[var(--green-dim)]' },
  item_launch_type_updated:    { icon: FileEdit,    color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  item_investment_type_updated: { icon: FileEdit,   color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  item_field_updated:          { icon: FileEdit,    color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  item_category_updated:       { icon: Circle,      color: 'text-[var(--green)]',    bgColor: 'bg-[var(--green-dim)]' },
  item_altitude_updated:       { icon: ArrowRight,  color: 'text-[var(--mustard)]',   bgColor: 'bg-[var(--mustard-dim)]' },
  item_dedupe_updated:         { icon: GitBranch,   color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]' },
  item_parent_updated:         { icon: GitBranch,   color: 'text-cyan-600',    bgColor: 'bg-cyan-100' },
  item_marked_complete:        { icon: CheckCircle, color: 'text-[var(--green)]', bgColor: 'bg-[var(--green-dim)]' },
  item_deleted:                { icon: AlertCircle, color: 'text-[var(--coral)]',     bgColor: 'bg-[var(--coral-dim)]' },
  item_split:                  { icon: GitBranch,   color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]' },
  item_added_to_cycle:         { icon: Calendar,    color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  item_removed_from_cycle:     { icon: Calendar,    color: 'text-[var(--mustard)]',   bgColor: 'bg-[var(--mustard-dim)]' },
  shaping_started:             { icon: FileEdit,    color: 'text-[var(--mustard)]',   bgColor: 'bg-[var(--mustard-dim)]' },
  shaping_completed:           { icon: CheckCircle, color: 'text-[var(--green)]', bgColor: 'bg-[var(--green-dim)]' },
  altitude_set:                { icon: ArrowRight,  color: 'text-[var(--mustard)]',   bgColor: 'bg-[var(--mustard-dim)]' },
  dedupe_resolved:             { icon: GitBranch,   color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]' },
  attribute_schema_attached:   { icon: FileEdit,    color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]' },
  group_renamed:               { icon: FileEdit,    color: 'text-[var(--mustard)]',  bgColor: 'bg-[var(--mustard-dim)]' },
  relationship_edge_added:     { icon: GitBranch,   color: 'text-cyan-600',    bgColor: 'bg-cyan-100' },
  relationship_edge_dissolved: { icon: GitBranch,   color: 'text-[var(--coral)]',     bgColor: 'bg-[var(--coral-dim)]' },
  item_imported:               { icon: Camera,      color: 'text-[var(--green)]',    bgColor: 'bg-[var(--green-dim)]' },
  // ── Cycle governance events (surfaced via get_item_cycle_trail) ──
  ranking_completed:           { icon: BarChart3,   color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]' },
  ranking_overridden:          { icon: AlertCircle, color: 'text-[var(--mustard)]',  bgColor: 'bg-[var(--mustard-dim)]' },
  criteria_updated:            { icon: FileEdit,    color: 'text-[var(--green)]',    bgColor: 'bg-[var(--green-dim)]' },
  criteria_changed:            { icon: FileEdit,    color: 'text-[var(--green)]',    bgColor: 'bg-[var(--green-dim)]' },
  criteria_locked:             { icon: Lock,        color: 'text-[var(--text-sec)]',   bgColor: 'bg-[var(--hover-tint)]' },
  criteria_unlocked:           { icon: Unlock,      color: 'text-[var(--text-ter)]',   bgColor: 'bg-[var(--surface)]' },
  candidate_pool_updated:      { icon: Layers,      color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  pool_modified:               { icon: Layers,      color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  ItemPoolLocked:              { icon: Lock,        color: 'text-[var(--text-sec)]',   bgColor: 'bg-[var(--hover-tint)]' },
  ItemPoolUnlocked:            { icon: Unlock,      color: 'text-[var(--text-ter)]',   bgColor: 'bg-[var(--surface)]' },
  ItemPoolCreated:             { icon: Layers,      color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  value_convergence_completed: { icon: CheckCircle, color: 'text-[var(--green)]', bgColor: 'bg-[var(--green-dim)]' },
  value_source_selected:       { icon: FileEdit,    color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]' },
  value_source_cleared:        { icon: FileEdit,    color: 'text-[var(--text-ter)]',  bgColor: 'bg-[var(--slate-dim)]' },
  PublishCompleted:            { icon: CheckCircle, color: 'text-[var(--green)]', bgColor: 'bg-[var(--green-dim)]' },
  PublishAttempted:            { icon: Upload,      color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  FocusListArchived:           { icon: Archive,     color: 'text-[var(--mustard)]',   bgColor: 'bg-[var(--mustard-dim)]' },
  session_created:             { icon: Users,       color: 'text-[var(--text-ter)]',    bgColor: 'bg-[var(--slate-dim)]' },
  cycle_published:             { icon: CheckCircle, color: 'text-[var(--green)]', bgColor: 'bg-[var(--green-dim)]' },
};

// ============================================================================
// Plain-words helpers
// ============================================================================

// ============================================================================
// Types
// ============================================================================

export interface HistoryTabProps {
  itemId: string;
  maxEvents?: number;
  className?: string;
}

// ============================================================================
// History table row
// ============================================================================

interface HistoryRowProps {
  event: AuditEntry;
  actorDisplayName: string;
  sessionNameMap: Record<string, string>;
  /** User id → display name, for owner and delegate changes. */
  nameMap?: Record<string, string>;
  layout?: 'table' | 'card';
  /** The item's category now, for naming an altitude whose event did not record one. */
  itemCategory?: string | null;
}

function HistoryRow({ event, actorDisplayName, sessionNameMap, nameMap, layout = 'table', itemCategory }: HistoryRowProps) {
  const getItemById = useItemsStore((s) => s.getItemById);

  const config = EVENT_CONFIG[event.eventType] ?? EVENT_CONFIG.UPDATE;
  const Icon = config.icon;

  const description = buildDescription(event, sessionNameMap, getItemById, itemCategory);

  const tsDate = new Date(event.timestamp);
  const shortDate = Number.isNaN(tsDate.getTime())
    ? '—'
    : new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(tsDate);

  // ── Inline detail line (field-level from/to) ──────────────────────────────
  const detail = useMemo((): React.ReactNode => {
    const lines = changeLines(event, getItemById, nameMap);
    if (lines.length === 0) return null;
    return (
      <span className="flex flex-wrap gap-x-3">
        {lines.slice(0, 3).map((line, i) => (
          <span key={i} className="text-[var(--text-ter)]">
            {line.label}:{' '}
            {line.from !== undefined && (
              <>
                <span className="text-[var(--text-ter)]">{line.from}</span>
                {' → '}
              </>
            )}
            <span className="text-[var(--text-sec)]">{line.to}</span>
          </span>
        ))}
        {lines.length > 3 && <span className="text-[var(--text-ter)]">+{lines.length - 3} more</span>}
      </span>
    );
  // The names arrive after the first draw; recompute then, or a new owner stays "Someone".
  }, [event, getItemById, nameMap]);

  // ── Context chips (session, cycle — no raw UUIDs) ─────────────────────────
  const contextChips = useMemo((): React.ReactNode[] => {
    const chips: React.ReactNode[] = [];
    const d = event.payload?.details as Record<string, unknown> | undefined;
    if (!d) return chips;

    if (d.sessionId && typeof d.sessionId === 'string') {
      const sessionName = sessionNameMap[d.sessionId] ?? 'Session';
      chips.push(
        <Link
          key="session"
          to={`/prioritization/sessions/${d.sessionId}`}
          className="inline-flex items-center gap-0.5 text-[var(--text-ter)] hover:underline"
        >
          <Layers className="h-2.5 w-2.5" />
          {sessionName}
        </Link>
      );
    }

    if (d.cycleId && typeof d.cycleId === 'string') {
      chips.push(
        <Link
          key="cycle"
          to={`/prioritization/cycles/${d.cycleId}`}
          className="inline-flex items-center gap-0.5 text-[var(--text-ter)] hover:underline"
        >
          <Calendar className="h-2.5 w-2.5" />
          Cycle
        </Link>
      );
    }

    // targetItemIds — resolve to item titles (no raw UUIDs)
    if (Array.isArray(d.targetItemIds) && (d.targetItemIds as string[]).length > 0) {
      const ids = (d.targetItemIds as string[]).slice(0, 3);
      chips.push(
        <span key="targets" className="inline-flex items-center gap-1">
          <GitBranch className="h-2.5 w-2.5 text-[var(--text-ter)]" />
          {ids.map((tid, i) => {
            const resolved = getItemById(tid);
            return (
              <Link
                key={tid}
                to={`/prioritization/items/${tid}`}
                className="text-[var(--text-ter)] hover:underline"
              >
                {resolved?.title
                  ? `"${resolved.title.slice(0, 30)}${resolved.title.length > 30 ? '…' : ''}"`
                  : `${tid.slice(0, 8)}…`}
                {i < ids.length - 1 ? ',' : ''}
              </Link>
            );
          })}
          {(d.targetItemIds as string[]).length > 3 && (
            <span className="text-[var(--text-ter)]">+{(d.targetItemIds as string[]).length - 3} more</span>
          )}
        </span>
      );
    }

    // promotionCohortId intentionally omitted — internal UUID, no display value

    return chips;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, sessionNameMap]);

  const hasSubtext = detail != null || contextChips.length > 0;

  // For events sourced from the item's cycle trail, show a "via cycle" link.
  const isCycleCtx = event.payload?._source === 'cycle';
  const cycleCtxId = isCycleCtx && typeof event.payload?._cycleId === 'string'
    ? event.payload._cycleId as string
    : null;

  const eventBody = (
    <div className="flex items-start gap-2">
      <span
        className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full ${config.bgColor} dark:opacity-90 flex items-center justify-center`}
      >
        <Icon className={`h-2.5 w-2.5 ${config.color}`} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-snug">{description}</p>
        {hasSubtext && (
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
            {detail}
            {contextChips}
          </div>
        )}
        {isCycleCtx && (
          <div className="mt-0.5">
            {cycleCtxId ? (
              <Link
                to={`/prioritization/cycles/${cycleCtxId}?tab=history`}
                className="inline-flex items-center gap-0.5 text-[10px] text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] hover:underline"
              >
                <Calendar className="h-2.5 w-2.5" />
                cycle activity
              </Link>
            ) : (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                <Calendar className="h-2.5 w-2.5" />
                cycle activity
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  if (layout === 'card') {
    return (
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-3">
        {eventBody}
        <div className="mt-2 pt-2 border-t border-[var(--rule)] dark:border-[var(--rule)] flex items-center justify-between gap-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <span className="truncate">{actorDisplayName}</span>
          <span className="flex-shrink-0 text-[var(--text-ter)] dark:text-[var(--text-dim)]">{shortDate}</span>
        </div>
      </div>
    );
  }

  return (
    <tr className="group border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0 hover:bg-[var(--surface)]/60 dark:hover:bg-[var(--hover-tint)] transition-colors">
      {/* Event + description */}
      <td className="py-2.5 pl-4 pr-3 align-top min-w-[200px]">
        {eventBody}
      </td>

      {/* Actor */}
      <td className="py-2.5 px-3 align-top whitespace-nowrap text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] max-w-[120px] truncate">
        {actorDisplayName}
      </td>

      {/* Timestamp */}
      <td className="py-2.5 pr-4 align-top whitespace-nowrap text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] text-right">
        {shortDate}
      </td>
    </tr>
  );
}

// ============================================================================
// HistoryTab Component
// ============================================================================

/**
 * Counts up once, 1.2 s after each save of this item (its `updatedAt` moves), so
 * the async Supabase write has landed before the caller re-reads the trail.
 * Stays at 0 until the first save — opening or switching items is not a save.
 * One trigger for History and the Timeline, so neither can lag the other.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useItemSaveTick(itemId: string): number {
  // Coerced to a number so Zustand's shallow equality catches Date changes.
  const itemUpdatedAt = useItemsStore((s) => {
    const item = s.getItemById(itemId);
    if (!item?.updatedAt) return 0;
    const d = item.updatedAt instanceof Date ? item.updatedAt : new Date(item.updatedAt as string);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  });
  const [tick, setTick] = useState(0);
  const lastSeenRef = useRef<{ itemId: string; updatedAt: number } | null>(null);

  // A different item starts from zero, which also cancels any read still in flight for the old one.
  useEffect(() => { setTick(0); }, [itemId]);

  useEffect(() => {
    const prev = lastSeenRef.current;
    lastSeenRef.current = { itemId, updatedAt: itemUpdatedAt };
    if (!prev || prev.itemId !== itemId || !itemUpdatedAt || prev.updatedAt === itemUpdatedAt) return;
    const timer = setTimeout(() => setTick((t) => t + 1), 1200);
    return () => clearTimeout(timer);
  }, [itemId, itemUpdatedAt]);

  return tick;
}

export function HistoryTab({ itemId, maxEvents, className = '' }: HistoryTabProps) {
  const fetchItemAuditTrail = useAuditStore((state) => state.fetchItemAuditTrail);
  const getEntriesForItem = useAuditStore((state) => state.getEntriesForItem);
  const getItemById = useItemsStore((state) => state.getItemById);
  const itemCategory = useItemsStore((state) => state.getItemById(itemId)?.itemCategory);
  const allSessions = useAllSessions();
  const sessionNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    allSessions.forEach((s) => { if (s.id && s.name) map[s.id] = s.name; });
    return map;
  }, [allSessions]);

  const [events, setEvents] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Ticks 1.2 s after any field save so the new event appears without a page reload.
  const saveTick = useItemSaveTick(itemId);

  // Initial fetch (and itemId navigation re-fetch) — no delay.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchItemAuditTrail(itemId)
      .then((entries) => {
        if (!cancelled) setEvents(maxEvents == null ? entries : entries.slice(0, maxEvents));
      })
      .catch(() => {
        if (!cancelled) {
          const fallback = getEntriesForItem(itemId);
          setEvents(maxEvents == null ? fallback : fallback.slice(0, maxEvents));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [itemId, maxEvents, fetchItemAuditTrail, getEntriesForItem]);

  // After a field save, re-fetch the trail.
  useEffect(() => {
    if (saveTick === 0) return;
    let cancelled = false;
    fetchItemAuditTrail(itemId)
      .then((entries) => {
        if (!cancelled) setEvents(maxEvents == null ? entries : entries.slice(0, maxEvents));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTick]);

  const HIDDEN_EVENT_TYPES = useMemo(() => new Set(['itempoolitemadded']), []);

  // Comments are part of the record: each one is a row here, in time order with
  // everything else, so the trail reads as one story (815).
  const allComments = useEntityCommentsStore((s) => s.comments);
  const fetchCommentsForEntity = useEntityCommentsStore((s) => s.fetchForEntity);
  useEffect(() => {
    if (itemId) void fetchCommentsForEntity('item', itemId);
  }, [itemId, fetchCommentsForEntity]);
  const commentEntries = useMemo<AuditEntry[]>(
    () =>
      allComments
        .filter((c) => c.entityType === 'item' && c.entityId === itemId)
        .map((c) => ({
          id: `comment-${c.id}`,
          eventType: 'comment_posted',
          timestamp: c.createdAt,
          userId: c.authorId,
          actorName: c.authorName,
          note: plainExcerpt(c.body, 160),
          payload: {
            commentId: c.id,
            isReply: !!c.replyToId,
            anchorLabel: c.anchorLabel ?? undefined,
            resolved: !!c.resolvedAt,
          },
        })),
    [allComments, itemId],
  );

  const item = getItemById(itemId);
  const displayEvents = useMemo(() => {
    const filtered = events.filter(
      (e) => !HIDDEN_EVENT_TYPES.has(String(e.eventType).toLowerCase().replace(/_/g, ''))
    );
    if (filtered.length > 0 || commentEntries.length > 0) {
      const merged = [...filtered, ...commentEntries];
      merged.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      return maxEvents == null ? merged : merged.slice(0, maxEvents);
    }
    if (!item?.createdAt) return [];
    const ts = item.createdAt instanceof Date ? item.createdAt.toISOString() : String(item.createdAt);
    const created: AuditEntry = {
      id: `created-${itemId}`,
      eventType: 'CREATE',
      timestamp: ts,
      actorName: item.createdBy ?? undefined,
      userId: item.createdBy ?? undefined,
      note: 'Item created',
    };
    return [created];
  }, [events, HIDDEN_EVENT_TYPES, item, itemId, commentEntries, maxEvents]);

  const actorIds = useMemo(
    () => [
      ...displayEvents.map((e) => e.userId).filter((id): id is string => !!id),
      ...namedPeopleIds(displayEvents),
    ],
    [displayEvents]
  );
  const actorDisplayMap = useActorDisplayMap(actorIds);

  const [searchQuery, setSearchQuery] = useState('');
  const countState = getItemCountState(displayEvents.length);
  const showSearch = countState === 'many' || searchQuery.length > 0;

  const filteredEvents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return displayEvents;
    return displayEvents.filter((event) => {
      const actor =
        actorDisplayMap[event.userId ?? ''] ?? event.actorName ?? event.userId ?? 'System';
      const desc = buildDescription(event, sessionNameMap, getItemById, itemCategory).toLowerCase();
      return (
        desc.includes(q)
        || actor.toLowerCase().includes(q)
        || event.eventType.toLowerCase().includes(q)
        || (event.note?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [displayEvents, searchQuery, actorDisplayMap, sessionNameMap, getItemById, itemCategory]);

  // Pages of events. A long-lived item has hundreds; show a page at a time.
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(filteredEvents.length / HISTORY_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  useEffect(() => { setPage(1); }, [itemId, searchQuery]);
  const pageStart = (currentPage - 1) * HISTORY_PAGE_SIZE;
  const pageEvents = useMemo(
    () => filteredEvents.slice(pageStart, pageStart + HISTORY_PAGE_SIZE),
    [filteredEvents, pageStart],
  );

  const renderRow = (event: AuditEntry, index: number) => (
    <HistoryRow
      key={`${event.id}-${index}`}
      event={event}
      actorDisplayName={
        actorDisplayMap[event.userId ?? ''] ?? event.actorName ?? event.userId ?? 'System'
      }
      sessionNameMap={sessionNameMap}
      nameMap={actorDisplayMap}
      itemCategory={itemCategory}
    />
  );

  const renderCard = (event: AuditEntry, index: number) => (
    <HistoryRow
      key={`${event.id}-${index}`}
      event={event}
      actorDisplayName={
        actorDisplayMap[event.userId ?? ''] ?? event.actorName ?? event.userId ?? 'System'
      }
      sessionNameMap={sessionNameMap}
      nameMap={actorDisplayMap}
      itemCategory={itemCategory}
      layout="card"
    />
  );

  return (
    <div className={className}>
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Loading history…</span>
        </div>
      ) : displayEvents.length === 0 ? (
        <div className="text-center py-8 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          <Clock className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
          <p className="text-sm">No history available</p>
        </div>
      ) : (
        <>
          {showSearch && (
            <div className="mb-3 space-y-1.5">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] dark:text-[var(--text-dim)]" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search history…"
                  aria-label="Search history events"
                  className="w-full pl-8 pr-8 py-2 text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-dim)] focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-primary-500/25"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-dim)] dark:hover:text-[var(--text-sec)]"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {searchQuery.trim() && (
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                  {filteredEvents.length} of {displayEvents.length} events
                </p>
              )}
            </div>
          )}

          {filteredEvents.length === 0 ? (
            <div className="text-center py-6 text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              <p className="text-sm">No events match your search.</p>
            </div>
          ) : (
            <>
              {/* Desktop / tablet: table with horizontal scroll safety */}
              <div className="hidden md:block overflow-x-auto -mx-1 px-1">
                <table className="w-full text-sm min-w-[480px]">
                  <thead>
                    <tr className="border-b border-[var(--rule)] dark:border-[var(--rule)]">
                      <th className="py-2 pl-4 pr-3 text-left text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wide">
                        Event
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wide">
                        Who
                      </th>
                      <th className="py-2 pr-4 text-right text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-dim)] uppercase tracking-wide">
                        When
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageEvents.map(renderRow)}
                  </tbody>
                </table>
              </div>

              {/* Phone: stacked cards */}
              <div className="md:hidden space-y-2">
                {pageEvents.map(renderCard)}
              </div>

              {pageCount > 1 && (
                <nav
                  className="mt-3 flex items-center justify-between gap-3 text-xs text-[var(--text-sec)]"
                  aria-label="History pages"
                >
                  <span>
                    {pageStart + 1}–{pageStart + pageEvents.length} of {filteredEvents.length} events
                  </span>
                  <span className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-2.5 py-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] hover:bg-[var(--hover-tint)] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Newer
                    </button>
                    <span aria-live="polite">Page {currentPage} of {pageCount}</span>
                    <button
                      type="button"
                      onClick={() => setPage(currentPage + 1)}
                      disabled={currentPage === pageCount}
                      className="px-2.5 py-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] hover:bg-[var(--hover-tint)] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Older
                    </button>
                  </span>
                </nav>
              )}
            </>
          )}
        </>
      )}

    </div>
  );
}

export default HistoryTab;
